import { useEffect, useState } from "react";
import { askAdminMe } from "../api.js";
import { useAccount } from "../auth/Gate.js";

/**
 * One question per account per page load; the link is convenience, the API is the guard.
 *
 * Keyed by the account, so signing in as somebody else asks again. Only a
 * definite answer stays cached: a network error or a 5xx is forgotten, so the
 * next mount retries rather than hiding the link until a reload.
 */
const cache = new Map<string, Promise<boolean | null>>();

function ask(key: string): Promise<boolean | null> {
  let answer = cache.get(key);
  if (!answer) {
    answer = askAdminMe();
    cache.set(key, answer);
    void answer.then((value) => {
      if (value === null && cache.get(key) === answer) cache.delete(key);
    });
  }
  return answer;
}

export function useIsAdmin(): boolean {
  const account = useAccount();
  const key = account?.email ?? null;
  const [admin, setAdmin] = useState(false);
  useEffect(() => {
    setAdmin(false);
    if (!key) return;
    let live = true;
    void ask(key).then((value) => {
      // Any error means not admin for this render.
      if (live) setAdmin(value === true);
    });
    return () => {
      live = false;
    };
  }, [key]);
  return admin;
}
